import React, { useState } from "react";
import { ChevronRight, ChevronDown, Plus, Trash2 } from "lucide-react";

// Immutable deep-set by path (array of keys/indexes)
export const setByPath = (obj, path, val) => {
  if (path.length === 0) return val;
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[head] = setByPath(obj[head], rest, val);
  return clone;
};

export const removeByPath = (obj, path) => {
  if (path.length === 1) {
    const key = path[0];
    if (Array.isArray(obj)) return obj.filter((_, i) => i !== key);
    const c = { ...obj };
    delete c[key];
    return c;
  }
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[head] = removeByPath(obj[head], rest);
  return clone;
};

const emptyLike = (val) => {
  if (Array.isArray(val)) return [];
  if (val && typeof val === "object") {
    const o = {};
    for (const k of Object.keys(val)) o[k] = emptyLike(val[k]);
    return o;
  }
  if (typeof val === "number") return 0;
  if (typeof val === "boolean") return false;
  return "";
};

const isPrimitive = (v) => v === null || typeof v !== "object";
const slug = (path) => path.join("-").replace(/[^a-zA-Z0-9-]/g, "_");

export default function JsonEditor({ data, section, onChange, defaultOpen = true }) {
  const [openSet, setOpenSet] = useState(() =>
    defaultOpen ? new Set([section]) : new Set()
  );

  const keyOf = (path) => path.join(".");
  const toggle = (path) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      const k = keyOf(path);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const change = (path, val) => onChange(path, val, false);
  const remove = (path) => onChange(path, undefined, true);
  const addItem = (path, arr) => {
    const template = arr.length ? emptyLike(arr[arr.length - 1]) : "";
    onChange(path, [...arr, template], false);
    setOpenSet((prev) => new Set(prev).add(keyOf(path)));
  };

  const renderPrimitive = (label, value, path, canRemove) => {
    const testid = `admin-field-${slug(path)}`;
    if (typeof value === "boolean") {
      return (
        <div className="flex items-center gap-2" key={keyOf(path)}>
          <label className="flex items-center gap-3 py-1.5 flex-1">
            <span className="text-sm font-medium text-stone-600 min-w-[140px]">{label}</span>
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => change(path, e.target.checked)}
              data-testid={testid}
              className="h-4 w-4 accent-red-800"
            />
          </label>
          {canRemove && (
            <button onClick={() => remove(path)} className="text-stone-400 hover:text-red-800" title="Usuń">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      );
    }
    const str = value === null ? "" : String(value);
    const isLong = str.length > 55 || str.includes("\n");
    return (
      <div className="flex items-start gap-2" key={keyOf(path)}>
        <div className="flex-1 py-1.5">
          <label className="block text-xs font-medium uppercase tracking-wide text-stone-400 mb-1">
            {label}
          </label>
          {isLong ? (
            <textarea
              value={str}
              onChange={(e) => change(path, e.target.value)}
              data-testid={testid}
              rows={Math.min(8, Math.max(2, str.split("\n").length))}
              className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 focus:border-red-800 focus:outline-none focus:ring-1 focus:ring-red-800/30"
            />
          ) : (
            <input
              type="text"
              value={str}
              onChange={(e) => change(path, e.target.value)}
              data-testid={testid}
              className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 focus:border-red-800 focus:outline-none focus:ring-1 focus:ring-red-800/30"
            />
          )}
        </div>
        {canRemove && (
          <button onClick={() => remove(path)} className="mt-6 text-stone-400 hover:text-red-800" title="Usuń">
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  };

  const renderNode = (label, value, path, depth, canRemove) => {
    if (isPrimitive(value)) return renderPrimitive(label, value, path, canRemove);

    const isArr = Array.isArray(value);
    const entries = isArr ? value.map((v, i) => [i, v]) : Object.entries(value);
    const open = openSet.has(keyOf(path));

    return (
      <div key={keyOf(path)} className={depth === 0 ? "" : "border-l border-stone-200 pl-3 ml-1"}>
        <div className="flex items-center gap-2 py-1">
          <button
            onClick={() => toggle(path)}
            className="flex items-center gap-1.5 text-left"
            data-testid={`admin-toggle-${slug(path)}`}
          >
            {open ? (
              <ChevronDown size={16} className="text-red-800" />
            ) : (
              <ChevronRight size={16} className="text-stone-400" />
            )}
            <span className="font-medium text-stone-700">{label}</span>
            <span className="text-xs text-stone-400">{isArr ? `[ ${entries.length} ]` : `{ ${entries.length} }`}</span>
          </button>
          {canRemove && (
            <button onClick={() => remove(path)} className="text-stone-400 hover:text-red-800" title="Usuń">
              <Trash2 size={15} />
            </button>
          )}
        </div>
        {open && (
          <div className="space-y-1 py-1">
            {entries.map(([k, v]) => renderNode(String(k), v, [...path, k], depth + 1, isArr))}
            {isArr && (
              <button
                onClick={() => addItem(path, value)}
                className="mt-1 flex items-center gap-1 text-xs font-medium text-red-800 hover:underline"
                data-testid={`admin-add-${slug(path)}`}
              >
                <Plus size={14} /> dodaj element
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return renderNode(section, data, [section], 0, false);
}
